import { Component, computed, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmInput } from '@spartan-ng/helm/input';
import { HlmButton } from '@spartan-ng/helm/button';
import { ThemedIconComponent } from '../themed-icon/themed-icon.component';

/**
 * InlineEditComponent - Click-to-edit text field.
 *
 * Display mode: shows text as a heading/paragraph (click to edit).
 * Edit mode: shows an input field with save/cancel buttons.
 */
@Component({
  selector: 'ocp-inline-edit',
  imports: [FormsModule, TranslatePipe, HlmButton, HlmInput, ThemedIconComponent],
  host: {
    class: 'inline-edit',
    '[class.inline-edit--editing]': 'isEditing()',
    '[class.inline-edit--disabled]': 'disabled()',
  },
  template: `
    @if (isEditing()) {
      <div class="inline-edit__editor">
        @if (multiline()) {
          <textarea
            #textareaEl
            hlmInput
            class="inline-edit__input inline-edit__textarea"
            [ngModel]="editValue()"
            (ngModelChange)="editValue.set($event)"
            [placeholder]="placeholder() | translate"
            (keydown.control.enter)="onSave()"
            (keydown.escape)="onCancel()"
            (blur)="onBlur()"
            rows="4"
          ></textarea>
        } @else {
          <input
            #inputEl
            hlmInput
            class="inline-edit__input"
            [ngModel]="editValue()"
            (ngModelChange)="editValue.set($event)"
            [placeholder]="placeholder() | translate"
            (keydown.control.enter)="onSave()"
            (keydown.enter)="onSave()"
            (keydown.escape)="onCancel()"
            (blur)="onBlur()"
          />
        }
        <div class="inline-edit__actions" [class.inline-edit__actions--absolute]="!multiline()">
          @if (multiline()) {
            <button
              hlmBtn
              type="button"
              [disabled]="!canSave()"
              (click)="onSave()"
              (mousedown)="$event.preventDefault()"
            >
              {{ 'common.save' | translate }}
            </button>
            <button
              hlmBtn
              type="button"
              variant="secondary"
              (click)="onCancel()"
              (mousedown)="$event.preventDefault()"
            >
              {{ 'common.cancel' | translate }}
            </button>
          } @else {
            <button
              hlmBtn
              type="button"
              variant="secondary"
              size="icon"
              class="inline-edit__icon-btn"
              [disabled]="!canSave()"
              [attr.aria-label]="'common.save' | translate"
              (click)="onSave()"
              (mousedown)="$event.preventDefault()"
            >
              <ocp-themed-icon name="checkmark.svg" [size]="18" />
            </button>
            <button
              hlmBtn
              type="button"
              variant="secondary"
              size="icon"
              class="inline-edit__icon-btn"
              [attr.aria-label]="'common.cancel' | translate"
              (click)="onCancel()"
              (mousedown)="$event.preventDefault()"
            >
              <ocp-themed-icon name="cross.svg" [size]="18" />
            </button>
          }
        </div>
      </div>
    } @else {
      <div
        class="inline-edit__display"
        [class.inline-edit__display--empty]="!value()"
        [attr.role]="disabled() ? undefined : 'button'"
        [attr.tabindex]="disabled() ? undefined : 0"
        (click)="onStartEdit()"
        (keydown.enter)="onStartEdit()"
        (keydown.space)="onStartEdit(); $event.preventDefault()"
      >
        <span class="inline-edit__text">{{ displayText() }}</span>
      </div>
    }
  `,
  styleUrl: './inline-edit.component.scss',
})
export class InlineEditComponent {
  readonly value = input<string>();
  readonly placeholder = input('');
  readonly emptyText = input('');
  readonly disabled = input(false);
  readonly multiline = input(false);

  readonly save = output<string>();
  readonly editCancel = output<void>();

  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');
  private readonly textareaEl = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl');

  protected readonly editValue = signal('');
  protected readonly isEditing = signal(false);

  protected readonly displayText = computed(() => this.value() || this.emptyText());

  protected readonly canSave = computed(() => this.editValue().trim().length > 0);

  protected onStartEdit(): void {
    if (this.disabled()) return;
    this.editValue.set(this.value() ?? '');
    this.isEditing.set(true);
    setTimeout(() => {
      if (this.multiline()) {
        this.textareaEl()?.nativeElement.focus();
      } else {
        this.inputEl()?.nativeElement.focus();
      }
    });
  }

  protected onSave(): void {
    const trimmed = this.editValue().trim();
    if (trimmed.length > 0 && trimmed !== this.value()) {
      this.save.emit(trimmed);
    }
    this.isEditing.set(false);
  }

  protected onBlur(): void {
    this.onCancel();
  }

  protected onCancel(): void {
    this.isEditing.set(false);
    this.editCancel.emit();
  }
}
